import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function Logo({ className = "", to = "/" }) {
  return (
    <Link to={to} className={`flex items-center gap-2 group ${className}`} data-testid="zk-logo">
      <span className="relative flex h-3 w-3">
        <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 zk-pulse" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-cyan-400" />
      </span>
      <span className="font-display text-lg font-bold tracking-tight text-slate-50">
        Zetta<span className="text-cyan-400">Keep</span>
      </span>
    </Link>
  );
}

const statusMap = {
  healthy: { c: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Healthy" },
  operational: { c: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Operational" },
  warning: { c: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Warning" },
  degraded: { c: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Degraded" },
  critical: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Critical" },
  offline: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Offline" },
  major_outage: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Major Outage" },
  partial_outage: { c: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Partial Outage" },
  provisioning: { c: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30", label: "Provisioning" },
  action_required: { c: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Finishing setup" },
  failed: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Failed" },
  deleting: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Deleting" },
  past_due: { c: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Past Due" },
  suspended: { c: "text-rose-400 bg-rose-500/10 border-rose-500/30", label: "Suspended" },
  active: { c: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Active" },
  none: { c: "text-slate-400 bg-slate-500/10 border-slate-600/30", label: "No servers" },
};

export function StatusBadge({ status, label }) {
  const s = statusMap[status] || { c: "text-slate-400 bg-slate-500/10 border-slate-600/30", label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${s.c}`} data-testid="status-badge">
      <span className={`h-1.5 w-1.5 rounded-full ${status === "provisioning" ? "zk-pulse" : ""}`} style={{ background: "currentColor" }} />
      {label || s.label}
    </span>
  );
}

export function GlassCard({ className = "", children, ...rest }) {
  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm transition-colors hover:border-slate-700 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Eyebrow({ children }) {
  return <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">{children}</div>;
}

export function CopyButton({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label || "Value"} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2 py-1 text-xs text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`h-5 w-5 animate-spin text-cyan-400 ${className}`} />;
}

export function PageLoader() {
  return <div className="flex h-[60vh] items-center justify-center"><Spinner className="h-8 w-8" /></div>;
}
